export interface IntersectedElements {
    readonly firstIntersecting?: IntersectionObserverEntry
    readonly next?: IntersectionObserverEntry
    readonly previous?: IntersectionObserverEntry
    readonly last?: IntersectionObserverEntry
    readonly lastVisible?: IntersectionObserverEntry
    readonly elements: IntersectionObserverEntry[]
}

export const calculateIntersection = async (scrollableElement: HTMLElement): Promise<IntersectedElements> =>
    createIntersectedElements(await getIntersectionObserverEntries(scrollableElement))

const getPrevious = (elements: IntersectionObserverEntry[]) => {
    const firstIntersecting = getFirstIntersecting(elements)
    if (isFullVisible(firstIntersecting)) {
        return elements.at(elements.indexOf(firstIntersecting) - 1)
    }
    return firstIntersecting
}
const getNext = (elements: IntersectionObserverEntry[]) => {
    return elements.at(elements.indexOf(getFirstIntersecting(elements)) + 1)
}
const getFirstIntersecting = (elements: IntersectionObserverEntry[]) => {
    return elements.find((e) => e.isIntersecting) as IntersectionObserverEntry
}
const getLast = (elements: IntersectionObserverEntry[]) => {
    return elements.at(-1)
}

const getLastVisible = (elements: IntersectionObserverEntry[]) => {
    return [...elements].reverse().find(isFullVisible)
}

export const getIntersectionObserverEntries = async (scrollableElement: HTMLElement) =>
    await new Promise<IntersectionObserverEntry[]>((resolve) => {
        const intersectionObserver = new IntersectionObserver(
            (entries) => {
                resolve(entries)
                /*
                  We want only the state found on function call.
                  We don't need the *observer*, but the *intersection* functionality
                  That's why we disconnect directly after the elements are available
                 */
                intersectionObserver.disconnect()
            },
            { root: scrollableElement },
        )
        Array.from(scrollableElement.children).forEach((e) => intersectionObserver.observe(e))
    })

const createIntersectedElements = (elements: IntersectionObserverEntry[]): IntersectedElements => ({
    get previous() {
        return getPrevious(elements)
    },
    get next() {
        return getNext(elements)
    },
    get firstIntersecting() {
        return getFirstIntersecting(elements)
    },
    get lastVisible() {
        return getLastVisible(elements)
    },
    get last() {
        return getLast(elements)
    },
    get elements() {
        return [...elements]
    },
})

const isFullVisible = (entry: IntersectionObserverEntry) => entry.intersectionRatio >= 0.99

export const scrollToElement = async (elementToScrollTo?: Element): Promise<void> => {
    if (!(elementToScrollTo instanceof HTMLElement)) {
        throw new Error('Can only scroll to HTMLElement')
    }
    const scrollableElement = elementToScrollTo.parentElement as HTMLElement
    const scrollableElementPaddingLeft = Number(
        window.getComputedStyle(scrollableElement).getPropertyValue('padding-left').replace('px', ''),
    )
    const scrollLeft = elementToScrollTo.offsetLeft - scrollableElementPaddingLeft
    const hasScrolled = () => Math.abs(scrollableElement.scrollLeft - scrollLeft) <= 1

    scrollableElement.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
    })

    await Promise.any([asyncWaitUntil(hasScrolled), waitTimeout(300)])
}

export const readjust = async (scrollableElement: HTMLElement) => {
    const { firstIntersecting, next, previous, last } = await calculateIntersection(scrollableElement)

    if (firstIntersecting?.intersectionRatio && isFullVisible(firstIntersecting)) {
        // first intersected is adjusted
        return
    }

    if (last?.intersectionRatio && isFullVisible(last)) {
        // last element is fully visible
        return
    }

    const scrollTo = (firstIntersecting?.intersectionRatio ?? 0) > 0.5 ? previous : next

    await scrollToElement(scrollTo?.target)
}

export const isScrolledToEnd = (scrollableElement: HTMLElement) => {
    return scrollableElement.scrollLeft + scrollableElement.offsetWidth >= scrollableElement.scrollWidth
}

export const isScrolledToStart = (scrollableElement: HTMLElement) => {
    return scrollableElement.scrollLeft === 0
}

export const scrollToNext = async (scrollableElement: HTMLElement) => {
    if (isScrolledToEnd(scrollableElement)) {
        return
    }
    const { next } = await calculateIntersection(scrollableElement)

    await scrollToElement(next?.target)
}

export const scrollToPrevious = async (scrollableElement: HTMLElement) => {
    if (isScrolledToStart(scrollableElement)) {
        return
    }
    const { previous } = await calculateIntersection(scrollableElement)

    await scrollToElement(previous?.target)
}

const waitTimeout = (timeout: number) => new Promise((resolve) => setTimeout(resolve, timeout))

const waitUntil = (predicate: () => boolean, cb: () => void) => {
    requestAnimationFrame(() => {
        if (predicate()) {
            cb()
        } else {
            waitUntil(predicate, cb)
        }
    })
}

const asyncWaitUntil = (predicate: () => boolean) =>
    new Promise((resolve) => {
        waitUntil(predicate, resolve as () => void)
    })
